
import { mapGetters } from 'vuex'
import EnerEchartsBar from '@/components/enerEcharts/enerEchartsBar'
import EnerEchartsLine from '@/components/enerEcharts/enerEchartsLine'
import enerOutlook from '@/api/eneroutlook'
import { getColorForRegionCode } from '@/utils/colorFunctions'
import chartType from '@/Enum/chartType'
import { getPivotArray } from '@/utils/enerFunctions'
import EnerLoader from '@/components/ui/enerLoader'
import { capitalize } from '@/utils/StringUtils'

export default {
  name: 'ChartContainer',
  components: { EnerEchartsBar, EnerEchartsLine, EnerLoader },
  props: {
    config: {
      type: Object,
      required: true
    },
    showLegend: {
      type: Boolean,
      required: false,
      default: true
    },
    showDownload: {
      type: Boolean,
      required: false,
      default: false
    },
    title: {
      required: false,
      type: String,
      default: ''
    },
    filename: {
      required: false,
      type: String,
      default: ''
    }
  },
  data () {
    return {
      apiData: [],
      isChartReady: false,
      exportMap: false,
      initOptions: {
        height: null
      },
      updateOptions: {
        notMerge: true
      }
    }
  },
  async fetch () {
    const serieFullCodes = this.config.items.map(item => item + '_' + this.config.displayUnit)
    const years = this.computedYears
    const scenarioCodes = this.config.scenario
    let zoneCodes
    if (this.config.countries.length > 0) {
      zoneCodes = this.config.countries
    } else if (this.benchmarkRegions.length > 0) {
      zoneCodes = this.benchmarkRegions.map(country => country.nrdCode)
    } else if (this.mapRegion !== this.world) {
      zoneCodes = [this.mapRegion.nrdCode]
    } else {
      switch (this.config.type) {
        case chartType.COLUMN_STACKED:
          zoneCodes = this.regions.map(region => region.nrdCode)
          break
        case chartType.DONUT:
          zoneCodes = ['monde']
          break
        case chartType.COLUMN:
          zoneCodes = this.regions.map(region => region.nrdCode)
          break
        case chartType.BAR:
          zoneCodes = ['monde']
          break
      }
    }
    const requestParams = {
      fullCode: serieFullCodes,
      zoneCode: zoneCodes,
      scenarioCode: scenarioCodes,
      year: years,
      pagination: false
    }
    this.apiData = await enerOutlook.values(requestParams, this)
  },
  computed: {
    ...mapGetters({
      regions: 'regions',
      countries: 'countries',
      world: 'world',
      defaultChartZoneCodes: 'defaultChartZoneCodes',
      mapRegion: 'page/mapRegion',
      benchmarkRegions: 'page/benchmarkRegions'
    }),
    computedYears () {
      if ((this.benchmarkRegions.length > 0 || this.mapRegion !== this.world) && this.config.years.length === 1 && this.config.type === chartType.COLUMN) {
        return []
      }
      return this.config.years
    },
    unitName () {
      return this.$options.filters.unitName(this.config.displayUnit)
    },
    numberFormat () {
      return this.config.nbDecimale
    },
    baseChartType () {
      switch (this.config.type) {
        case chartType.DONUT:
          return 'pie'
        case chartType.LINE:
          return 'line'
        default :
          return 'bar'
      }
    },
    isStacked () {
      return this.config.type === chartType.COLUMN_STACKED && this.benchmarkRegions.length === 0
    },
    categoryField () {
      if (this.computedYears.length === 1) {
        if (this.config.items.length === 1) {
          return 'zoneName'
        } else {
          return 'year'
        }
      } else {
        return 'year'
      }
    },
    chartDataset () {
      let chartDataset = []
      let regionCodes = []
      const categoryField = this.categoryField
      let nameField = 'zoneCodeNrd'
      let valueField = 'value'
      if (this.apiData.length > 0) {
        switch (this.config.type) {
          case chartType.COLUMN_STACKED:
          case chartType.COLUMN:
          case chartType.BAR:
          case chartType.LINE:
            // If only one year use zone as category
            if (this.computedYears.length === 1) {
              if (this.config.items.length === 1) {
                nameField = 'year'
                valueField = 'value'
              } else {
                nameField = 'serieShortTitle'
                valueField = 'value'
              }
            }
            if (this.config.countries.length > 0) {
              regionCodes = this.config.countries
            } else if (this.benchmarkRegions.length > 0) {
              regionCodes = this.benchmarkRegions.map(item => item.nrdCode)
            } else if (this.mapRegion !== this.world) {
              regionCodes = [this.mapRegion.nrdCode]
            } else {
              regionCodes = this.defaultChartZoneCodes
            }
            chartDataset = getPivotArray(this.apiData, categoryField, nameField, valueField)
            chartDataset = chartDataset.filter(item => item[0] === categoryField || regionCodes.includes(item[0]))
            chartDataset.sort((a, b) => {
              if (a[0] === categoryField) {
                return -1
              } else if (b[0] === categoryField) {
                return 1
              } else {
                return regionCodes.indexOf(a[0]) - regionCodes.indexOf(b[0])
              }
            })
            break
        }
      }
      return chartDataset
    },
    chartSeries () {
      const chartSeries = []
      // if (this.computedYears.length === 1) {
      //   if (this.config.items.length === 1) {
      //     const chartSerie = {
      //       category: this.categoryField,
      //       value: this.computedYears[0]
      //     }
      //     chartSeries.push(chartSerie)
      //   } else {
      //     const n = this.config.items.length
      //     for (let i = 0; i < n; i++) {
      //       const item = this.config.items[i]
      //       const energyCode = getEnergyCode(item)
      //       const chartSerie = {
      //         category: this.categoryField,
      //         value: getShortTitleForEnergyCode(energyCode, this.$i18n),
      //         color: getColorForEnergyName(energyCode)
      //       }
      //       chartSeries.push(chartSerie)
      //     }
      //   }
      // }
      if (this.config.countries.length > 0) {
        const n = this.config.countries.length
        for (let i = 0; i < n; i++) {
          const country = this.countries.find(country => country.nrdCode === this.config.countries[i])
          const chartSerie = {
            name: country['name' + capitalize(this.$i18n.locale)],
            type: this.baseChartType,
            seriesLayoutBy: 'row'
          }
          chartSeries.push(chartSerie)
        }
      } else if (this.benchmarkRegions.length > 0) {
        const n = this.benchmarkRegions.length
        for (let i = 0; i < n; i++) {
          const region = this.benchmarkRegions[i]
          const chartSerie = {
            name: region['name' + capitalize(this.$i18n.locale)],
            type: this.baseChartType,
            seriesLayoutBy: 'row'
          }
          chartSeries.push(chartSerie)
        }
      } else if (this.mapRegion !== this.world) {
        const chartSerie = {
          name: this.mapRegion['name' + capitalize(this.$i18n.locale)],
          type: this.baseChartType,
          seriesLayoutBy: 'row'
        }
        chartSeries.push(chartSerie)
      } else {
        const n = this.defaultChartZoneCodes.length
        for (let i = 0; i < n; i++) {
          const chartZoneCode = this.defaultChartZoneCodes[i]
          const region = this.regions.find(region => region.nrdCode === chartZoneCode)
          const chartSerie = {
            name: region['name' + capitalize(this.$i18n.locale)],
            type: this.baseChartType,
            color: getColorForRegionCode(region.nrdCode),
            seriesLayoutBy: 'row'
          }
          if (this.isStacked) {
            chartSerie.stack = 'total'
          }
          chartSeries.push(chartSerie)
        }
      }
      return chartSeries
    },
    option () {
      const scope = this
      const option = {
        tooltip: {
          trigger: 'item',
          showDelay: 0,
          transitionDuration: 0.2,
          formatter (params) {
            if (params.data === undefined) {
              return null
            }
            return params.seriesName + ' ' + params.data[0] + ': ' + params.data[params.seriesIndex + 1].toFixed(scope.nbDecimal) + ' ' + scope.unitName
          }
        },
        dataset: {
          source: this.chartDataset
        },
        xAxis: {
          type: 'category'
        },
        yAxis: {
          name: this.unitName,
          nameLocation: 'center',
          nameGap: 50,
          nameRotate: 90,
          nameTextStyle: {
            fontWeight: 'bold',
            fontSize: 14
          }
        },
        series: this.chartSeries
      }
      if (this.showLegend) {
        option.legend = {
          top: 'bottom'
        }
      }
      return option
    }

  },
  methods: {
    handleExportMap () {
      if (this.exportMap === false) {
        this.exportMap = true
      }
    },
    handleExportingMap () {
      if (this.exportMap === true) {
        this.exportMap = false
      }
    }
  },
  // watch: {
  //   mapRegion () {
  //     this.$fetch()
  //   },
  //   benchmarkRegions () {
  //     this.$fetch()
  //   }
  // },
  fetchOnServer: false
}
